/**
 * @fileoverview ScheduleRatesDialog — set new rates for every grade from one
 * date (MOR-1018).
 *
 * Covers both of WLPR's cases with one screen: the financial year rollover
 * (the date defaults to the next 1 July) and a mid-year change from a specific
 * date, such as rates a court approved "from 9 September onwards". The new
 * rates are added as dated rows, so they start charging on the date and time
 * already recorded keeps the rate it was recorded at.
 */

import { useState } from "react";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { roundToStep } from "@cvx/timesheets/rateMath";

import { extractErrorMessage } from "@/utils/errors";
import { Button, Label } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  financialYearLabel,
  formatIsoDate,
  formatRate,
  nextFinancialYearStart,
} from "./rateCards.helpers";

interface GradeRow {
  _id: Id<"staffGrades">;
  name: string;
  currentRate?: number;
}

export function ScheduleRatesDialog({
  open,
  grades,
  today,
  onOpenChange,
}: {
  open: boolean;
  grades: GradeRow[];
  today: string;
  onOpenChange: (open: boolean) => void;
}) {
  const addRate = useMutation(api.timesheets.grades.addRate);
  const [effectiveFrom, setEffectiveFrom] = useState(() =>
    nextFinancialYearStart(today),
  );
  const [rates, setRates] = useState<Record<string, string>>({});
  const [increase, setIncrease] = useState("");
  const [saving, setSaving] = useState(false);

  // Each session starts from the next 1 July with nothing filled in, so a
  // grade is only touched when the admin types a rate for it. The reset runs
  // on CLOSE, not on open: the dialog focuses the date field as it opens, and
  // a date that arrived after that focus would show blank and be wiped on
  // blur (the picker only syncs its text while unfocused).
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setEffectiveFrom(nextFinancialYearStart(today));
      setRates({});
      setIncrease("");
    }
    onOpenChange(next);
  };

  const changes = grades.flatMap((g) => {
    const raw = rates[g._id]?.trim() ?? "";
    const hourlyRate = Number(raw);
    return raw !== "" && Number.isFinite(hourlyRate) && hourlyRate >= 0
      ? [{ gradeId: g._id, hourlyRate }]
      : [];
  });
  const canSave = !!effectiveFrom && changes.length > 0;
  const isFyStart = effectiveFrom.endsWith("-07-01");

  const applyIncrease = () => {
    const percent = Number(increase);
    if (!Number.isFinite(percent)) return;
    setRates(
      Object.fromEntries(
        grades.flatMap((g) =>
          g.currentRate !== undefined
            ? [
                [
                  g._id,
                  String(
                    roundToStep(g.currentRate * (1 + percent / 100), undefined),
                  ),
                ],
              ]
            : [],
        ),
      ),
    );
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await Promise.all(
        changes.map((change) => addRate({ ...change, effectiveFrom })),
      );
      toast.success(
        `${changes.length === 1 ? "1 rate" : `${changes.length} rates`} set from ${formatIsoDate(effectiveFrom)}.`,
      );
      handleOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the new rates."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Set new rates from a date</DialogTitle>
          <DialogDescription>
            New rates start charging on the date you choose. Time already
            recorded before then keeps its rate. Jobs with frozen rates are not
            affected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Effective from</Label>
              <DatePicker
                value={effectiveFrom}
                onChange={(isoDate) => setEffectiveFrom(isoDate ?? "")}
                placeholder="Effective from"
              />
              {effectiveFrom && (
                <p className="text-xs text-muted-foreground">
                  {isFyStart
                    ? `Start of ${financialYearLabel(effectiveFrom)}.`
                    : `Mid-year change, during ${financialYearLabel(effectiveFrom)}.`}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="schedule-rates-increase">
                Increase all by (%)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="schedule-rates-increase"
                  type="number"
                  step={0.5}
                  value={increase}
                  onChange={(e) => setIncrease(e.target.value)}
                  placeholder="Optional"
                  className="tabular-nums"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0"
                  disabled={increase.trim() === ""}
                  onClick={applyIncrease}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border/60">
            <div className="grid grid-cols-[1fr_7rem_8rem] items-center gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <span>Grade</span>
              <span className="text-right">Current</span>
              <span>New hourly rate</span>
            </div>
            {grades.map((g) => (
              <div
                key={g._id}
                className="grid grid-cols-[1fr_7rem_8rem] items-center gap-2 px-3 py-1.5 text-sm"
              >
                <span className="truncate">{g.name}</span>
                <span className="text-right tabular-nums text-muted-foreground">
                  {g.currentRate !== undefined
                    ? formatRate(g.currentRate)
                    : "No rate"}
                </span>
                <Input
                  type="number"
                  min={0}
                  value={rates[g._id] ?? ""}
                  onChange={(e) =>
                    setRates((prev) => ({ ...prev, [g._id]: e.target.value }))
                  }
                  placeholder="Leave as is"
                  className="h-8 tabular-nums"
                  aria-label={`New hourly rate for ${g.name}`}
                />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSave || saving}
            onClick={() => void handleSave()}
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {changes.length > 0
              ? `Set ${changes.length === 1 ? "1 rate" : `${changes.length} rates`}`
              : "Set rates"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
